import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import * as variables from 'pubweb-smokey/dist/components/GridSystem/_vars_widths.js';
import { SubNavBarStyles } from './SubNavBar.styled';
import ChevronLftSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-lft.svg';
import ChevronRightSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-right.svg';
import SubNav from './SubNav.js';
import SearchPosts from './SearchPosts';
import StudioLogoSvg from './studio_logo.svg';
import { getCurrentCategory } from '@services/studioService';

const SCROLL_RATE = 5;
const SCROLL_DEAD_ZONE = 10;

const SubNavBar = ({
  posts,
  articleNavs,
  inspiredNavs,
  collectionNavs,
  storiesNavs,
  faqNavs,
}) => {
  /* eslint-disable react/no-unescaped-entities */
  const [activeFilterObj, setActiveFilterObj] = useState({});

  const [horizontalScroll, setHorizontalScroll] = useState(0);
  const [movingScroll, setMovingScroll] = useState(null);
  const [intervalFunction, setIntervalFunction] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const filterRef = useRef(null);
  const isScrollingRef = useRef(false);
  const navbarScrollWidth = useRef(0); // the scroll width of nav bar (filterRef) with no dropdowns open.

  const isMobile =
    typeof window !== 'undefined' &&
    window.innerWidth < variables.desktop_breakpoint;

  const closeAllOtherFilters = (openedFilter, isOpen) => {
    if (openedFilter) {
      let actFilt = activeFilterObj;
      actFilt[openedFilter] = isOpen;

      let isFilterOpen = false;
      Object.values(activeFilterObj).forEach((v) => (isFilterOpen |= v));

      setMenuOpen(isFilterOpen);
      setActiveFilterObj(actFilt);
    }
  };

  const scrollFilter = (value) => {
    filterRef.current.scrollLeft += value;
    setHorizontalScroll(filterRef.current.scrollLeft);
  };

  const clearIntervalScroll = () => {
    if (intervalFunction !== null) {
      clearInterval(intervalFunction);
      setIntervalFunction(null);
    }
    setMovingScroll(null);
  };

  const touchStart = (value) => {
    clearIntervalScroll();
    const scrollFunc = () => {
      scrollFilter(value);
    };
    setMovingScroll(value);
    setIntervalFunction(setInterval(scrollFunc, 20));
  };

  const touchEnd = () => {
    clearIntervalScroll();
  };

  const touchSubNavBar = () => {
    clearIntervalScroll();
    setHorizontalScroll(filterRef.current.scrollLeft);
  };

  // If you scroll with a fast swipe on a mobile device, the arrow sometimes remains displayed on the end.
  // This sets the horiz scroll one more time after a delay for the scroll bounce.
  // Might be able to use overscroll-behavior: none, but that isn't supported on safari.
  useEffect(() => {
    const scrollListenInt = setInterval(() => {
      if (isScrollingRef.current) {
        isScrollingRef.current = false;
        setHorizontalScroll(filterRef.current.scrollLeft);
      }
    }, 550);

    const scrollListen = () => {
      isScrollingRef.current = true;
    };
    filterRef.current.addEventListener('scroll', scrollListen);

    return () => {
      if (filterRef && filterRef.current) {
        filterRef.current.removeEventListener('scroll', scrollListen);
      }
      clearInterval(scrollListenInt);
    };
  }, [filterRef]);

  // Get and save scroll width value for the nav bar when all dropdowns are closed.
  // If we use scroll width from filterRef, the scroll width will get larger if you open
  // the dropdowns that are further on the right because their content would technically
  // be off the page (but we adjust their x-position to be in view). This was making the
  // right arrow display when you opened the right most drop down.
  useEffect(() => {
    if (
      !menuOpen &&
      navbarScrollWidth.current !== filterRef.current.scrollWidth
    ) {
      navbarScrollWidth.current = filterRef.current.scrollWidth;
    }
  }, [horizontalScroll, navbarScrollWidth, filterRef, menuOpen]);

  //Used to clear the intervalFunction in the case a long touch gets to a point where the arrow disappears.
  const leftScrollFinished =
    isMobile && movingScroll < 0 && horizontalScroll - SCROLL_DEAD_ZONE < 0;
  const rightScrollFinished =
    isMobile &&
    movingScroll > 0 &&
    navbarScrollWidth?.current &&
    horizontalScroll + SCROLL_DEAD_ZONE >
      navbarScrollWidth?.current - filterRef.current.clientWidth;

  if (leftScrollFinished || rightScrollFinished) {
    clearIntervalScroll();
  }

  const selectedArticleCat = getCurrentCategory();
  /* eslint-disable react/no-unescaped-entities */
  return (
    <SubNavBarStyles
      $isMenuOpen={menuOpen}
      ref={filterRef}
      onTouchMove={touchSubNavBar}
    >
      <div
        className="filters"
        onContextMenu={(ev) => {
          if (isMobile) {
            ev.preventDefault();
          }
        }}
      >
        <div
          className="left-scroll"
          onTouchStart={() => {
            touchStart(-SCROLL_RATE);
          }}
          onTouchEnd={() => {
            touchEnd();
          }}
        >
          {horizontalScroll - SCROLL_DEAD_ZONE > 0 ? <ChevronLftSvg /> : null}
        </div>

        <div className="logo-search-container">
          <div className="studio-logo">
            <div className="studio-logo-inner">
              <Link href={'/studio/'} aria-label="studio home">
                <StudioLogoSvg viewBox="0 0 40 18" />
              </Link>
            </div>
          </div>

          <SearchPosts posts={posts} closeHandler={closeAllOtherFilters} />
        </div>

        <div className="filter-articles filter">
          <SubNav
            navText="Articles"
            navId="articles"
            navItems={articleNavs}
            closeHandler={closeAllOtherFilters}
            selectedItem={selectedArticleCat?.categoryName}
          />
        </div>

        <div className="filter-collections filter">
          <SubNav
            navText="Collections"
            navId="collections"
            navItems={collectionNavs}
            closeHandler={closeAllOtherFilters}
          />
        </div>

        <div className="filter-inspiration filter">
          <SubNav
            navText="Inspiration"
            navId="inspiration"
            navItems={inspiredNavs}
            closeHandler={closeAllOtherFilters}
          />
        </div>

        <div className="filter-stories filter">
          <SubNav
            navText="Stories"
            navId="stories"
            navItems={storiesNavs}
            closeHandler={closeAllOtherFilters}
          />
        </div>

        <div className="filter-faqs filter">
          <SubNav
            navText="FAQs"
            navId="FAQs"
            navItems={faqNavs}
            closeHandler={closeAllOtherFilters}
          />
        </div>

        {/* virtual tour is not being used right now */}

        {/* <div className='filter filter-virtualtour'>
                    <SubNav
                        navText='Virtual Tour'
                        navId='virtualtour'
                        navItems={virtualTourNavs}
                        closeHandler={closeAllOtherFilters}
                    />
                </div> */}

        <div
          className="right-scroll"
          onTouchStart={() => {
            touchStart(SCROLL_RATE);
          }}
          onTouchEnd={() => {
            touchEnd();
          }}
        >
          {navbarScrollWidth?.current &&
          horizontalScroll + SCROLL_DEAD_ZONE <
            navbarScrollWidth?.current - filterRef.current.clientWidth ? (
            <ChevronRightSvg />
          ) : null}
        </div>
      </div>
    </SubNavBarStyles>
  );
};

SubNavBar.propTypes = {
  articleNavs: PropTypes.any,
  collectionNavs: PropTypes.any,
  inspiredNavs: PropTypes.any,
  posts: PropTypes.any,
  storiesNavs: PropTypes.any,
  faqNavs: PropTypes.any,
};

export default SubNavBar;
